import { unreachable, assertDefined, isOfType } from '../../../../ts-utils';
import { INIT_REPLY, CANCEL_CREATING_REPLY, EDIT_REPLY, CREATE_REPLY_REQUEST, CREATE_REPLY_FAILURE, CREATE_REPLY_SUCCESS, DELETE_REPLY_REQUEST, DELETE_REPLY_SUCCESS, DELETE_REPLY_FAILURE, UPDATE_REPLY_REQUEST, UPDATE_REPLY_FAILURE, UPDATE_REPLY_SUCCESS, CANCEL_EDITING_REPLY, EDIT_REPLY_CREATE_ERROR, } from './replies-crud-actions';
import { isReplyStateIN } from './reply-types';
import { omit } from 'lodash';
import { hiddenComponentEffectsGuard } from '../effects/hidden-component-effects-guard';
import { getChildrenIds } from './replies-selectors';
export function repliesCrudReducer(state, action) {
    switch (action.type) {
        case INIT_REPLY: {
            const { localId, targetId } = action.payload;
            const parentId = getParentFromTarget(state.commentsById, targetId);
            if (!parentId) {
                return state;
            }
            const repliesState = state.replyLists[parentId];
            const isDuplication = (repliesState.newRepliesToReplies[targetId] || []).some((id) => state.commentsById[id].type === 'REPLY_TYPING') ||
                repliesState.chunks
                    .filter(isOfType('NEW'))
                    .some((c) => state.commentsById[c.localOrCommentId].type === 'REPLY_TYPING');
            if (isDuplication) {
                return state;
            }
            // We can have only one reply in 'REPLY_TYPING' state, so we clear others
            const clearedReplyState = state.replyLists[parentId];
            const isReplyingToParent = targetId === parentId;
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: { type: 'REPLY_TYPING', localId, targetId, parentId },
                },
                replyLists: {
                    ...state.replyLists,
                    [parentId]: {
                        ...clearedReplyState,
                        chunks: isReplyingToParent
                            ? [...clearedReplyState.chunks, { type: 'NEW', localOrCommentId: localId }]
                            : clearedReplyState.chunks,
                        newRepliesToReplies: isReplyingToParent
                            ? clearedReplyState.newRepliesToReplies
                            : {
                                ...clearedReplyState.newRepliesToReplies,
                                [targetId]: [localId, ...(clearedReplyState.newRepliesToReplies[targetId] || [])],
                            },
                    },
                },
            };
        }
        case CANCEL_CREATING_REPLY: {
            const { localId, targetId } = action.payload;
            const currentState = state.commentsById[localId];
            if (!currentState ||
                !isOfType(['REPLY_TYPING', 'REPLY_CREATE_ERROR_EDITING'], currentState)) {
                return state;
            }
            const isReplyingToParent = targetId === currentState.parentId;
            const repliesState = state.replyLists[currentState.parentId];
            return {
                ...state,
                commentsById: omit(state.commentsById, localId),
                replyLists: {
                    ...state.replyLists,
                    [currentState.parentId]: {
                        ...repliesState,
                        chunks: isReplyingToParent
                            ? repliesState.chunks.filter((c) => !(isOfType('NEW', c) && c.localOrCommentId === localId))
                            : repliesState.chunks,
                        newRepliesToReplies: isReplyingToParent
                            ? repliesState.newRepliesToReplies
                            : {
                                ...state.replyLists[currentState.parentId].newRepliesToReplies,
                                [targetId]: (state.replyLists[currentState.parentId].newRepliesToReplies[targetId] || []).filter((replyId) => replyId !== localId),
                            },
                    },
                },
            };
        }
        case EDIT_REPLY_CREATE_ERROR: {
            const { localId, authorName, email } = action.payload;
            const currentState = state.commentsById[localId];
            if (!isOfType(['REPLY_CREATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: { ...currentState, type: 'REPLY_CREATE_ERROR_EDITING', authorName, email },
                },
            };
        }
        case CANCEL_EDITING_REPLY: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'REPLY_EDITING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        // we do not spread currentState to not pass content field
                        type: 'REPLY_READY',
                        comment: currentState.comment,
                        commentId: currentState.commentId,
                        reactions: currentState.reactions,
                        parentId: currentState.parentId,
                        targetId: currentState.targetId,
                        targetStatus: currentState.targetStatus,
                        replyingToTargetLoading: currentState.replyingToTargetLoading,
                        marked: currentState.marked,
                        ...(currentState.localId ? { localId: currentState.localId } : {}),
                        isModerationPending: currentState.comment.status === 'PENDING',
                    },
                },
            };
        }
        case EDIT_REPLY: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['REPLY_READY', 'REPLY_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'REPLY_EDITING' },
                },
            };
        }
        case UPDATE_REPLY_REQUEST: {
            const { commentId, content } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['REPLY_EDITING', 'REPLY_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        ...currentState,
                        type: 'REPLY_UPDATE_PENDING',
                        content,
                    },
                },
            };
        }
        case UPDATE_REPLY_SUCCESS: {
            const { comment, commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'REPLY_UPDATE_PENDING') {
                return state;
            }
            assertDefined(comment);
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [comment.commentId]: {
                        ...currentState,
                        type: 'REPLY_READY',
                        comment,
                        isModerationPending: comment.status === 'PENDING' || !!comment.draftContent,
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_SUCCESS', id: comment.commentId, timestamp: Date.now() },
                    modal: comment.status === 'PENDING' || comment.draftContent
                        ? { type: 'pending-comment', timestamp: Date.now() }
                        : state.effects?.modal,
                }),
            };
        }
        case UPDATE_REPLY_FAILURE: {
            const { message, commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'REPLY_UPDATE_PENDING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'REPLY_UPDATE_ERROR', errorMessage: message },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_ERROR', id: commentId, timestamp: Date.now() },
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                    modal: message.type === 'MODAL'
                        ? { type: message.modalType, timestamp: Date.now() }
                        : state.effects?.modal,
                }),
            };
        }
        case CREATE_REPLY_REQUEST: {
            const { localId, content, targetId, asGuest, authorName } = action.payload;
            const parentId = getParentFromTarget(state.commentsById, targetId);
            if (!parentId) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [localId]: {
                        type: 'REPLY_CREATE_PENDING',
                        localId,
                        content,
                        targetId,
                        targetStatus: getTargetCommentStatus(state.commentsById, targetId),
                        parentId,
                        asGuest,
                        authorName,
                    },
                },
            };
        }
        case CREATE_REPLY_SUCCESS: {
            const { comment, localId, targetId } = action.payload;
            assertDefined(comment);
            const pendingComment = state.commentsById[localId];
            if (!isOfType('REPLY_CREATE_PENDING', pendingComment)) {
                return state;
            }
            const repliesState = state.replyLists[pendingComment.parentId];
            const replyState = {
                type: 'REPLY_READY',
                comment,
                commentId: comment.commentId,
                localId,
                targetId,
                targetStatus: getTargetCommentStatus(state.commentsById, targetId),
                parentId: pendingComment.parentId,
                reactions: { total: 0, reactions: [], type: 'IDLE' },
                marked: { type: 'IDLE' },
                replyingToTargetLoading: false,
                isModerationPending: comment.status === 'PENDING',
            };
            const isReplyingToParent = targetId === replyState.parentId;
            return {
                ...state,
                totals: state.totals + 1,
                commentsById: {
                    ...omit(state.commentsById, localId),
                    [comment.commentId]: replyState,
                },
                replyLists: {
                    ...state.replyLists,
                    [pendingComment.parentId]: {
                        ...repliesState,
                        chunks: isReplyingToParent
                            ? repliesState.chunks.map((c) => isOfType('NEW', c) && c.localOrCommentId === localId
                                ? { type: 'NEW', localOrCommentId: replyState.commentId }
                                : c)
                            : repliesState.chunks,
                        newRepliesToReplies: isReplyingToParent
                            ? repliesState.newRepliesToReplies
                            : {
                                ...repliesState.newRepliesToReplies,
                                [targetId]: repliesState.newRepliesToReplies[targetId].map((id) => id === localId ? comment.commentId : id),
                            },
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    ...state,
                    highlight: { type: 'CRUD_SUCCESS', id: comment.commentId, timestamp: Date.now() },
                    toast: {
                        isHidden: true,
                        skin: 'success',
                        message: { type: 'TRANSLATION', key: 'toast.reply-published' },
                        timestamp: Date.now(),
                    },
                    modal: comment.status === 'PENDING'
                        ? { type: 'pending-comment', timestamp: Date.now() }
                        : state.effects?.modal,
                }),
            };
        }
        case CREATE_REPLY_FAILURE: {
            const { message, localId, authorName, email } = action.payload;
            const currentState = state.commentsById[localId];
            if (currentState.type === 'REPLY_CREATE_PENDING') {
                return {
                    ...state,
                    commentsById: {
                        ...state.commentsById,
                        [localId]: {
                            ...currentState,
                            type: 'REPLY_CREATE_ERROR',
                            errorMessage: message,
                            authorName,
                            email,
                        },
                    },
                    ...hiddenComponentEffectsGuard(state, {
                        highlight: { type: 'CRUD_ERROR', id: localId, timestamp: Date.now() },
                        toast: message.type === 'TOAST'
                            ? { skin: 'error', message: message.message, timestamp: Date.now() }
                            : state.effects?.toast,
                        modal: message.type === 'MODAL'
                            ? { type: message.modalType, timestamp: Date.now() }
                            : state.effects?.modal,
                    }),
                };
            }
            return state;
        }
        case DELETE_REPLY_REQUEST: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (!isOfType(['REPLY_READY', 'REPLY_UPDATE_ERROR'], currentState)) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: { ...currentState, type: 'REPLY_DELETE_PENDING' },
                },
            };
        }
        case DELETE_REPLY_SUCCESS: {
            const { commentId } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'REPLY_DELETE_PENDING') {
                return state;
            }
            const childrenIds = getChildrenIds(state.commentsById)(commentId);
            const updatedReplies = childrenIds.reduce((acc, id) => {
                const commentState = state.commentsById[id];
                if ('targetStatus' in commentState && 'commentId' in commentState) {
                    acc[commentState.commentId] = { ...commentState, targetStatus: 'DELETED' };
                }
                return acc;
            }, {});
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    ...updatedReplies,
                    [commentId]: { ...currentState, type: 'REPLY_DELETED' },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_SUCCESS', id: commentId, timestamp: Date.now() },
                }),
                totals: state.totals - 1,
            };
        }
        case DELETE_REPLY_FAILURE: {
            const { commentId, message } = action.payload;
            const currentState = state.commentsById[commentId];
            if (currentState.type !== 'REPLY_DELETE_PENDING') {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [commentId]: {
                        ...currentState,
                        type: 'REPLY_READY',
                        isModerationPending: currentState.comment.status === 'PENDING',
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'CRUD_ERROR', id: commentId, timestamp: Date.now() },
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        default:
            throw unreachable(action);
    }
}
const getParentFromTarget = (commentsById, targetId) => {
    const target = commentsById[targetId];
    if (!target) {
        return undefined;
    }
    if (isReplyStateIN(target)) {
        return target.parentId;
    }
    if ('commentId' in target) {
        return target.commentId;
    }
    return undefined;
};
const getTargetCommentStatus = (commentsById, targetId) => {
    const target = commentsById[targetId];
    if (!target) {
        return 'UNKNOWN';
    }
    switch (target.type) {
        case 'REPLY_READY':
            return 'PUBLISHED';
        case 'REPLY_DELETED':
            return 'DELETED';
        default:
            return 'UNKNOWN';
    }
};
//# sourceMappingURL=replies-crud-reducer.js.map